// src/pages/SiteLocationMaster.tsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  Button,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from 'reactstrap';
import {
  Plus,
  Edit,
  Trash2,
  RefreshCw,
  Eye,
  CheckCircle,
  XCircle,
  Search,
  Navigation,
  MapPin,
  Crosshair,
  Briefcase,
} from 'react-feather';
import BreadCrumb from '../../Components/Common/BreadCrumb';
import TableContainer from '../../Components/Common/TableContainer';
import MapPicker from '../../Components/mappicker';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { apiClient } from '../../helpers/api_helper';

interface SiteLocation {
  id: number;
  site_name: string;
  project_name: string | null;
  address: string | null;
  latitude: number;
  longitude: number;
  radius: number;
  status: number;
  created_at: string;
  updated_at: string;
  _count?: {
    employee_locations: number;
    attendanceLogs: number;
  };
}

interface FormData {
  site_name: string;
  project_name: string;
  address: string;
  latitude: string;
  longitude: string;
  radius: string;
  status: boolean;
}

const SiteLocationMaster: React.FC = () => {
  // State
  const [locations, setLocations] = useState<SiteLocation[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [projectFilter, setProjectFilter] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Modal states
  const [modal, setModal] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');
  const [selectedLocation, setSelectedLocation] = useState<SiteLocation | null>(null);
  const [formData, setFormData] = useState<FormData>({
    site_name: '',
    project_name: '',
    address: '',
    latitude: '20.5937',
    longitude: '78.9629',
    radius: '100',
    status: true,
  });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');

  // Delete modal
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState<SiteLocation | null>(null);

  // View modal
  const [viewModal, setViewModal] = useState(false);
  const [viewingLocation, setViewingLocation] = useState<SiteLocation | null>(null);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const params: any = {
        page: currentPage + 1,
        limit: pageSize,
        sort_by: 'site_name',
        sort_order: 'asc',
      };

      if (searchTerm) {
        params.search = searchTerm;
      }

      if (projectFilter) {
        params.project_name = projectFilter;
      }

      if (!showInactive) {
        params.status = 1;
      }

      const response = await apiClient.get('/locations/site', { params });
      const data = response.data || response;
      const locationsData = data.data || [];
      const pageInfo = data.pagination || {};

      setLocations(locationsData);
      setTotalCount(pageInfo.totalCount || 0);
      setCurrentPage((pageInfo.page || 1) - 1);
      setPageSize(pageInfo.limit || 10);
    } catch (error: any) {
      console.error('Error fetching site locations:', error);
      setError(error.message || 'Failed to fetch site locations');
      toast.error('Failed to load site locations');
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, searchTerm, projectFilter, showInactive]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  // Handle search debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (currentPage === 0) {
        fetchLocations();
      } else {
        setCurrentPage(0);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm, projectFilter]);

  const handlePageChange = (page: number) => setCurrentPage(page);
  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(0);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleMapLocationChange = (lat: number, lng: number) => {
    setFormData(prev => ({
      ...prev,
      latitude: lat.toFixed(6),
      longitude: lng.toFixed(6),
    }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      setFormError('Geolocation is not supported by your browser');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setFormData(prev => ({
          ...prev,
          latitude: pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
      },
      () => {
        setFormError('Could not retrieve your location');
      }
    );
  };

  const openAddModal = () => {
    setModalMode('add');
    setFormData({
      site_name: '',
      project_name: '',
      address: '',
      latitude: '20.5937',
      longitude: '78.9629',
      radius: '100',
      status: true,
    });
    setFormError('');
    setModal(true);
  };

  const openEditModal = (location: SiteLocation) => {
    setModalMode('edit');
    setSelectedLocation(location);
    setFormData({
      site_name: location.site_name,
      project_name: location.project_name || '',
      address: location.address || '',
      latitude: location.latitude.toFixed(6),
      longitude: location.longitude.toFixed(6),
      radius: location.radius.toString(),
      status: location.status === 1,
    });
    setFormError('');
    setModal(true);
  };

  const closeModal = () => {
    setModal(false);
    setSelectedLocation(null);
    setFormError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.site_name.trim()) {
      setFormError('Site name is required');
      return;
    }

    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);
    const rad = parseFloat(formData.radius);

    if (isNaN(lat) || lat < -90 || lat > 90) {
      setFormError('Invalid latitude (-90 to 90)');
      return;
    }

    if (isNaN(lng) || lng < -180 || lng > 180) {
      setFormError('Invalid longitude (-180 to 180)');
      return;
    }

    if (isNaN(rad) || rad <= 0) {
      setFormError('Radius must be a positive number');
      return;
    }

    setSubmitting(true);
    setFormError('');

    try {
      const payload = {
        site_name: formData.site_name.trim(),
        project_name: formData.project_name.trim() || undefined,
        address: formData.address.trim() || undefined,
        latitude: lat,
        longitude: lng,
        radius: rad,
        status: formData.status ? 1 : 0,
      };

      if (modalMode === 'add') {
        await apiClient.post('/locations/site', payload);
        toast.success('Site location created successfully!');
      } else {
        await apiClient.put(`/locations/site/${selectedLocation?.id}`, payload);
        toast.success('Site location updated successfully!');
      }

      closeModal();
      fetchLocations();
    } catch (error: any) {
      console.error('Error saving site location:', error);
      setFormError(error.message || 'Failed to save site location');
      toast.error('Failed to save site location');
    } finally {
      setSubmitting(false);
    }
  };

  const openDeleteModal = (location: SiteLocation) => {
    setLocationToDelete(location);
    setDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!locationToDelete) return;

    setDeleting(true);
    try {
      await apiClient.delete(`/locations/site/${locationToDelete.id}`);
      toast.success('Site location deleted successfully!');
      setDeleteModal(false);
      fetchLocations();
    } catch (error: any) {
      console.error('Error deleting site location:', error);
      toast.error(error.message || 'Failed to delete site location');
    } finally {
      setDeleting(false);
      setLocationToDelete(null);
    }
  };

  const handleView = (location: SiteLocation) => {
    setViewingLocation(location);
    setViewModal(true);
  };

  const formatDate = (dateString: string) => {
    try {
      return format(new Date(dateString), 'dd MMM yyyy HH:mm');
    } catch {
      return 'N/A';
    }
  };

  const getStatusBadge = (status: number) => {
    return status === 1 ? (
      <Badge color="success">
        <CheckCircle size={12} className="me-1" />
        Active
      </Badge>
    ) : (
      <Badge color="warning">
        <XCircle size={12} className="me-1" />
        Inactive
      </Badge>
    );
  };

  const columns = useMemo(
    () => [
      {
        header: 'SI.No',
        accessorKey: 'id',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const { row } = cell;
          return <strong>{currentPage * pageSize + row.index + 1}</strong>;
        },
      },
      {
        header: 'Site Name',
        accessorKey: 'site_name',
        cell: (cell: any) => (
          <span className="fw-semibold">{cell.getValue()}</span>
        ),
      },
      {
        header: 'Project',
        accessorKey: 'project_name',
        cell: (cell: any) => cell.getValue() || '—',
      },
      {
        header: 'Location',
        accessorKey: 'latitude',
        cell: (cell: any) => {
          const row = cell.row.original;
          return (
            <div className="text-muted small">
              <div>{row.latitude.toFixed(4)}, {row.longitude.toFixed(4)}</div>
              <div className="text-primary">
                <MapPin size={12} className="me-1" />
                {row.radius}m radius
              </div>
            </div>
          );
        },
      },
      {
        header: 'Employees',
        accessorKey: '_count.employee_locations',
        cell: (cell: any) => {
          const count = cell.getValue() || 0;
          return (
            <Badge color="info" pill>
              {count} {count === 1 ? 'Employee' : 'Employees'}
            </Badge>
          );
        },
      },
      {
        header: 'Status',
        accessorKey: 'status',
        cell: (cell: any) => getStatusBadge(cell.getValue()),
      },
      {
        header: 'Actions',
        accessorKey: 'actions',
        enableColumnFilter: false,
        enableSorting: false,
        cell: (cell: any) => {
          const location = cell.row.original;
          return (
            <div className="d-flex gap-1">
              <Button
                color="info"
                size="sm"
                onClick={() => handleView(location)}
                className="d-flex align-items-center"
              >
                <Eye size={14} />
              </Button>
              <Button
                color="primary"
                size="sm"
                onClick={() => openEditModal(location)}
                className="d-flex align-items-center"
              >
                <Edit size={14} />
              </Button>
              <Button
                color="danger"
                size="sm"
                onClick={() => openDeleteModal(location)}
                className="d-flex align-items-center"
              >
                <Trash2 size={14} />
              </Button>
            </div>
          );
        },
      },
    ],
    [currentPage, pageSize]
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="Site Location Master" pageTitle="Locations" />

        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <h5 className="card-title mb-0">Site Locations</h5>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <div className="position-relative" style={{ width: '200px' }}>
                    <Input
                      type="text"
                      placeholder="Search sites..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="ps-4"
                      bsSize="sm"
                    />
                    <Search
                      size={14}
                      className="position-absolute top-50 translate-middle-y text-muted"
                      style={{ left: '10px' }}
                    />
                  </div>

                  <Input
                    type="text"
                    placeholder="Filter by project..."
                    value={projectFilter}
                    onChange={(e) => setProjectFilter(e.target.value)}
                    bsSize="sm"
                    style={{ width: '180px' }}
                  />

                  <FormGroup check className="mb-0 d-flex align-items-center">
                    <Label check className="me-2 mb-0">
                      <Input
                        type="checkbox"
                        checked={showInactive}
                        onChange={() => {
                          setShowInactive(!showInactive);
                          setCurrentPage(0);
                        }}
                      />
                      <span className="ms-1">Show Inactive</span>
                    </Label>
                  </FormGroup>

                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => fetchLocations()}
                    disabled={loading}
                  >
                    <RefreshCw size={14} className="me-1" />
                    Refresh
                  </Button>

                  <Button
                    color="primary"
                    size="sm"
                    onClick={openAddModal}
                  >
                    <Plus size={14} className="me-1" />
                    Add Site
                  </Button>
                </div>
              </CardHeader>

              <CardBody>
                {error && (
                  <Alert color="danger" className="mb-3">
                    {error}
                  </Alert>
                )}

                {loading ? (
                  <div className="text-center py-5">
                    <Spinner color="primary" className="me-2" />
                    <span>Loading site locations...</span>
                  </div>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={locations}
                    customPageSize={pageSize}
                    tableClass="table-striped"
                    theadClass="table-light"
                    manualPagination={true}
                    pageCount={Math.ceil(totalCount / pageSize)}
                    currentPage={currentPage}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    totalRecords={totalCount}
                    showPageSizeOptions={true}
                    pageSizeOptions={[10, 25, 50, 100]}
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Add/Edit Modal with Map */}
      <Modal isOpen={modal} toggle={closeModal} size="xl">
        <ModalHeader toggle={closeModal}>
          {modalMode === 'add' ? 'Add Site Location' : 'Edit Site Location'}
        </ModalHeader>
        <ModalBody>
          {formError && (
            <Alert color="danger" className="mb-3">
              {formError}
            </Alert>
          )}
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col lg={7}>
                <div className="border rounded-3 overflow-hidden" style={{ height: '450px' }}>
                  <MapPicker
                    latitude={parseFloat(formData.latitude) || 20.5937}
                    longitude={parseFloat(formData.longitude) || 78.9629}
                    radius={parseFloat(formData.radius) || 100}
                    isEditing={true}
                    onLocationChange={handleMapLocationChange}
                    height="450px"
                  />
                </div>
              </Col>
              <Col lg={5}>
                <div className="d-flex flex-column h-100 gap-3">
                  <FormGroup>
                    <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                      Site Name *
                    </Label>
                    <Input
                      type="text"
                      name="site_name"
                      value={formData.site_name}
                      onChange={handleInputChange}
                      bsSize="sm"
                      placeholder="e.g. Construction Site A"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                      Project Name
                    </Label>
                    <Input
                      type="text"
                      name="project_name"
                      value={formData.project_name}
                      onChange={handleInputChange}
                      bsSize="sm"
                      placeholder="e.g. Green Valley Project"
                    />
                  </FormGroup>

                  <FormGroup>
                    <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                      Address
                    </Label>
                    <Input
                      type="textarea"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      bsSize="sm"
                      rows={2}
                      placeholder="Street address (optional)"
                    />
                  </FormGroup>

                  <Row>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                          Latitude
                        </Label>
                        <Input
                          type="number"
                          name="latitude"
                          value={formData.latitude}
                          onChange={handleInputChange}
                          bsSize="sm"
                          placeholder="20.5937"
                          step="any"
                        />
                      </FormGroup>
                    </Col>
                    <Col md={6}>
                      <FormGroup>
                        <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                          Longitude
                        </Label>
                        <Input
                          type="number"
                          name="longitude"
                          value={formData.longitude}
                          onChange={handleInputChange}
                          bsSize="sm"
                          placeholder="78.9629"
                          step="any"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  <Button
                    color="info"
                    size="sm"
                    outline
                    onClick={handleUseCurrentLocation}
                    className="w-100 d-flex align-items-center justify-content-center gap-2"
                  >
                    <Crosshair size={14} />
                    Use Current Location
                  </Button>

                  <FormGroup>
                    <Label className="form-label-sm fw-bold text-muted text-uppercase small">
                      Radius (meters)
                    </Label>
                    <div className="d-flex gap-2 flex-wrap">
                      {[50, 100, 200, 500].map((r) => (
                        <Button
                          key={r}
                          color={formData.radius === String(r) ? 'primary' : 'light'}
                          size="sm"
                          onClick={() => setFormData(prev => ({ ...prev, radius: String(r) }))}
                          className="flex-grow-1"
                        >
                          {r}m
                        </Button>
                      ))}
                    </div>
                    <Input
                      type="number"
                      name="radius"
                      value={formData.radius}
                      onChange={handleInputChange}
                      bsSize="sm"
                      className="mt-2"
                      placeholder="Custom radius"
                      min="1"
                    />
                  </FormGroup>

                  <FormGroup check>
                    <Label check>
                      <Input
                        type="checkbox"
                        name="status"
                        checked={formData.status}
                        onChange={handleInputChange}
                      />
                      <span className="ms-2">Active</span>
                    </Label>
                  </FormGroup>
                </div>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={closeModal} disabled={submitting}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Saving...
              </>
            ) : (
              modalMode === 'add' ? 'Create' : 'Update'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={deleteModal} toggle={() => setDeleteModal(false)}>
        <ModalHeader toggle={() => setDeleteModal(false)}>
          Confirm Delete
        </ModalHeader>
        <ModalBody>
          <p>
            Are you sure you want to delete the site{' '}
            <strong>"{locationToDelete?.site_name}"</strong>?
          </p>
          {locationToDelete?._count?.employee_locations && locationToDelete._count.employee_locations > 0 && (
            <Alert color="danger" className="mb-0">
              <i className="ri-alert-line me-2"></i>
              This site has {locationToDelete._count.employee_locations} employee(s) assigned.
              You cannot delete it until you reassign these employees.
            </Alert>
          )}
          {(!locationToDelete?._count?.employee_locations || locationToDelete._count.employee_locations === 0) && (
            <Alert color="warning" className="mb-0">
              <i className="ri-alert-line me-2"></i>
              This will deactivate the site location. You can restore it later.
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="light" onClick={() => setDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            color="danger"
            onClick={confirmDelete}
            disabled={deleting || (locationToDelete?._count?.employee_locations || 0) > 0}
          >
            {deleting ? (
              <>
                <Spinner size="sm" className="me-2" />
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </Button>
        </ModalFooter>
      </Modal>

      {/* View Modal */}
      <Modal isOpen={viewModal} toggle={() => setViewModal(false)} size="lg">
        <ModalHeader toggle={() => setViewModal(false)}>
          Site Location Details
        </ModalHeader>
        <ModalBody>
          {viewingLocation && (
            <div>
              <div className="border rounded-3 overflow-hidden mb-4" style={{ height: '300px' }}>
                <MapPicker
                  latitude={viewingLocation.latitude}
                  longitude={viewingLocation.longitude}
                  radius={viewingLocation.radius}
                  isEditing={false}
                  height="300px"
                />
              </div>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Site Name</strong>
                </Col>
                <Col md={8}>
                  <span className="fw-semibold">{viewingLocation.site_name}</span>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Project</strong>
                </Col>
                <Col md={8}>
                  <span className="fw-semibold">{viewingLocation.project_name || '—'}</span>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Address</strong>
                </Col>
                <Col md={8}>
                  {viewingLocation.address || '—'}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Coordinates</strong>
                </Col>
                <Col md={8}>
                  <span className="font-monospace">
                    {viewingLocation.latitude.toFixed(6)}, {viewingLocation.longitude.toFixed(6)}
                  </span>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Radius</strong>
                </Col>
                <Col md={8}>
                  {viewingLocation.radius} meters
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Status</strong>
                </Col>
                <Col md={8}>
                  {getStatusBadge(viewingLocation.status)}
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Employees</strong>
                </Col>
                <Col md={8}>
                  <Badge color="info" pill>
                    {viewingLocation._count?.employee_locations || 0} Employees
                  </Badge>
                </Col>
              </Row>

              <Row className="mb-2">
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Created At</strong>
                </Col>
                <Col md={8}>
                  {formatDate(viewingLocation.created_at)}
                </Col>
              </Row>

              <Row>
                <Col md={4}>
                  <strong className="text-muted small text-uppercase">Updated At</strong>
                </Col>
                <Col md={8}>
                  {formatDate(viewingLocation.updated_at)}
                </Col>
              </Row>
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setViewModal(false)}>
            Close
          </Button>
          {viewingLocation && (
            <Button
              color="primary"
              onClick={() => {
                setViewModal(false);
                openEditModal(viewingLocation);
              }}
            >
              <Edit size={14} className="me-1" />
              Edit
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default SiteLocationMaster;